/**
 * Tractor maths (M2-10, spec §6.3 step 4): the hold point, the pull toward it, which freed part the tractor grabs
 * and whether a captured part fits. Pure and allocation-free (D-014); float64 world positions (D-011).
 *
 * The runtime (M2-17) applies `tractorAccel` to the part body before the Rapier step, releases the part when it
 * leaves `tractorRange`, and runs `captureCheck` when `inCaptureRange` says the part reached the hold point.
 */
import type { Vec3 } from '../core/math'
import type { SalvageConfig } from '../data/m2Schemas'
import { SALVAGE_STATUS, type PartPool, type SalvageStatus } from '../space/contracts'
import { wreckToWorld } from './layout'

export type TractorConfig = SalvageConfig['tractor']

/** The part-pool columns the tractor picker reads. */
export type TractorPool = Pick<PartPool, 'high' | 'alive' | 'freed' | 'secured' | 'x' | 'y' | 'z'>

/** Below this distance (m) the pull direction is undefined and the part only matches the ship's velocity. */
const MIN_PULL_DISTANCE = 1e-6

/** World position of the tractor hold point for a ship at `s` with rotation `q` (float64). */
export function holdPointWorld(
  sx: number,
  sy: number,
  sz: number,
  qx: number,
  qy: number,
  qz: number,
  qw: number,
  cfg: TractorConfig,
  out: Vec3,
): Vec3 {
  return wreckToWorld(sx, sy, sz, qx, qy, qz, qw, cfg.holdPoint[0], cfg.holdPoint[1], cfg.holdPoint[2], out)
}

/**
 * Acceleration (m/s², into `out`) that pulls a part at `p` moving at `pv` toward the hold point `h` of a ship moving
 * at `sv`. The part's speed relative to the ship aims for `min(maxSpeed, √(2·pullAccel·distance))` toward the hold
 * point (so it arrives without overshooting), reached within one tick of `dt` where possible, and the acceleration
 * is capped at `pullAccel`. Returns the distance to the hold point.
 */
export function tractorAccel(
  px: number,
  py: number,
  pz: number,
  pvx: number,
  pvy: number,
  pvz: number,
  hx: number,
  hy: number,
  hz: number,
  svx: number,
  svy: number,
  svz: number,
  dt: number,
  cfg: TractorConfig,
  out: Vec3,
): number {
  const rx = hx - px
  const ry = hy - py
  const rz = hz - pz
  const dist = Math.sqrt(rx * rx + ry * ry + rz * rz)
  let wx = 0
  let wy = 0
  let wz = 0
  if (dist > MIN_PULL_DISTANCE) {
    const brake = Math.sqrt(2 * cfg.pullAccel * dist)
    const speed = brake < cfg.maxSpeed ? brake : cfg.maxSpeed
    wx = (rx / dist) * speed
    wy = (ry / dist) * speed
    wz = (rz / dist) * speed
  }
  // Velocity error relative to the ship, turned into an acceleration and capped.
  const inv = dt > 0 ? 1 / dt : 0
  let ax = (wx - (pvx - svx)) * inv
  let ay = (wy - (pvy - svy)) * inv
  let az = (wz - (pvz - svz)) * inv
  const mag = Math.sqrt(ax * ax + ay * ay + az * az)
  if (mag > cfg.pullAccel) {
    const k = cfg.pullAccel / mag
    ax *= k
    ay *= k
    az *= k
  }
  out.x = ax
  out.y = ay
  out.z = az
  return dist
}

/**
 * The freed, unsecured part nearest the reticle direction `d` (unit) from `o` within `range` metres and the half-angle
 * `cone` (radians), or -1. Ties go to the nearer part.
 */
export function tractorPick(
  pool: TractorPool,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  range: number,
  cone: number,
): number {
  const minCos = Math.cos(cone)
  const range2 = range * range
  let best = -1
  let bestCos = -1
  let bestDist2 = Infinity
  for (let p = 0; p < pool.high; p++) {
    if (pool.alive[p] === 0 || pool.freed[p] === 0 || pool.secured[p] !== 0) continue
    const rx = pool.x[p] - ox
    const ry = pool.y[p] - oy
    const rz = pool.z[p] - oz
    const dist2 = rx * rx + ry * ry + rz * rz
    if (dist2 > range2) continue
    const cos = dist2 > 0 ? (rx * dx + ry * dy + rz * dz) / Math.sqrt(dist2) : 1
    if (cos < minCos) continue
    if (best < 0 || cos > bestCos || (cos === bestCos && dist2 < bestDist2)) {
      best = p
      bestCos = cos
      bestDist2 = dist2
    }
  }
  return best
}

/** The part at `p` is within `captureDistance` of the hold point `h`. */
export function inCaptureRange(px: number, py: number, pz: number, hx: number, hy: number, hz: number, cfg: TractorConfig): boolean {
  const dx = hx - px
  const dy = hy - py
  const dz = hz - pz
  return dx * dx + dy * dy + dz * dz <= cfg.captureDistance * cfg.captureDistance
}

/** The part at `p` is within the tractor's `range` of the emitter at `o` (the runtime releases it beyond). */
export function inTractorRange(px: number, py: number, pz: number, ox: number, oy: number, oz: number, range: number): boolean {
  const dx = px - ox
  const dy = py - oy
  const dz = pz - oz
  return dx * dx + dy * dy + dz * dz <= range * range
}

/**
 * Whether a captured part enters the hold (spec §6.3): `tooLarge` when the part itself (`massT`) exceeds the
 * racks' `maxPartMassT`, `holdFull` when what it takes in the hold (`holdT`: a pod carries its contents too) exceeds
 * the free tonnes, else `ok`. The size check comes first, so the HUD says "TOO LARGE FOR HOLD" even when full.
 */
export function captureCheck(massT: number, maxPartMassT: number, cargoFreeT: number, holdT: number = massT): SalvageStatus {
  if (massT > maxPartMassT) return SALVAGE_STATUS.tooLarge
  if (holdT > cargoFreeT) return SALVAGE_STATUS.holdFull
  return SALVAGE_STATUS.ok
}
